import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import {
  EPropertyTypeId,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  DisplayText,
  Loading,
  Switch,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import { findLastIndex } from "lodash";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import * as t from "services/translationService";
import { getSteps } from "../../../Applications/PropertyDetailApplications.util";
import { RentMatchWordmark } from "../../../ApplicationsRentmatch/components/RentMatchWordmark";
import * as S from "./PropertyCandidatesCard.styles";

export interface IPropertyCandidatesCardRentMatch {
  /** Whether the current user may change the publication state. */
  canEdit: boolean;
  /** Called with the desired next publication state when the toggle is flipped. */
  onTogglePublish: (nextPublished: boolean) => void;
}

interface IPropertyCandidatesCardProps {
  property: IProperty;
  rentMatch?: IPropertyCandidatesCardRentMatch;
}

export const PropertyCandidatesCard: React.FC<IPropertyCandidatesCardProps> = ({
  property,
  rentMatch,
}) => {
  const navigate = useNavigate();
  const { broker } = authHooks.useSelf();

  const isGroup = property.typeId === EPropertyTypeId.Group;

  const { countSteps, isFetching } = applicationHooks.useCountSteps({
    shouldRefetch: true,
    query: isGroup
      ? undefined
      : {
          filter: {
            archivedAt: { is: null },
            propertyId: { eq: property.id },
          },
        },
  });

  const hasCalendarSync = useBrokerFeature(EBrokerFeature.CalendarSync);
  const hasRentMatch = useBrokerFeature(EBrokerFeature.RentMatch);

  const intakeType = broker?.intakeType || 2;
  const steps = getSteps(intakeType, hasCalendarSync, hasRentMatch);

  let activeApplicationsCount = 0;
  if (countSteps?.steps) {
    Object.keys(countSteps.steps).forEach(key => {
      if (+key !== EIntakeStep.notEligible && +key !== EIntakeStep.tenant) {
        activeApplicationsCount += countSteps.steps[+key];
      }
    });
  }

  const handleStepClick = (step: EIntakeStep) => {
    navigate(`/properties/${property.id}/applications`, { state: { step } });
  };

  return (
    <S.Root>
      <S.CardContainer>
        <S.InfoSection>
          <DisplayText size="medium" space="none">
            {getLocalizedText("rentmatch.dashboard.candidates")} (
            {activeApplicationsCount})
          </DisplayText>
        </S.InfoSection>

        <S.FunnelWrapper>
          <S.PipelineContainer>
            {isFetching && !countSteps ? (
              <Loading asDots={true} />
            ) : (
              <>
                {(() => {
                  const allSteps = steps.flat();
                  const lastActiveStepIndex = countSteps?.steps
                    ? findLastIndex(allSteps, step => !!countSteps.steps[step])
                    : -1;

                  return allSteps.map((step, index) => {
                    const isFirst = index === 0;
                    const isLast = allSteps.length - 1 === index;

                    const isCurrentActive = index <= lastActiveStepIndex;
                    const isNextActive = index + 1 <= lastActiveStepIndex;
                    let count = countSteps?.steps?.[step] || 0;

                    // Aggregate hidden application counts into the first step (New)
                    if (isFirst && countSteps?.steps) {
                      Object.keys(countSteps.steps).forEach(key => {
                        const stepKey = +key;
                        if (
                          stepKey !== EIntakeStep.notEligible &&
                          stepKey !== EIntakeStep.tenant &&
                          !allSteps.includes(stepKey)
                        ) {
                          count += countSteps.steps[stepKey] || 0;
                        }
                      });
                    }

                    const label = t.applicationStateTitle(step) || "";

                    return (
                      <Tooltip
                        key={step}
                        tooltipContent={label}
                        placement="bottom"
                        style={{ flex: 1, height: "100%" }}
                      >
                        <S.StepItemWrap>
                          {!isFirst && (
                            <S.LineLeft isActive={isCurrentActive} />
                          )}
                          {!isLast && <S.LineRight isActive={isNextActive} />}
                          <S.StepBubble
                            isActive={isCurrentActive}
                            onClick={() => handleStepClick(step)}
                          >
                            {count}
                          </S.StepBubble>
                        </S.StepItemWrap>
                      </Tooltip>
                    );
                  });
                })()}
              </>
            )}
          </S.PipelineContainer>
        </S.FunnelWrapper>

        {rentMatch && (
          <S.PublicationContainer>
            <RentMatchWordmark height={16} />
            <Switch
              isChecked={!!property.isRentMatchPublished}
              isDisabled={!rentMatch.canEdit}
              onChange={rentMatch.onTogglePublish}
            />
          </S.PublicationContainer>
        )}
      </S.CardContainer>
    </S.Root>
  );
};
